import { useState, useMemo } from "react";

/**
 * Хук для управления фильтрацией и поиском лидов
 * @param {Array} leads - массив всех лидов
 * @param {string} managerTg - Telegram username менеджера
 * @param {string} role - роль пользователя (admin/manager)
 * @returns {Object} - filtered leads и управление фильтрами
 */
export function useLeadFilters(leads, managerTg, role) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    event: "",
    type: "",
    works: "",
    cm: ""
  });

  // Мемоизированный список после применения фильтров
  const filteredLeads = useMemo(() => {
    return leads.filter((lead) => {
      // Фильтр по ивенту
      const eventMatch = filters.event ? lead.event === filters.event : true;
      
      // Фильтр по типу лида
      const typeMatch = filters.type 
        ? (lead.leadType === filters.type || lead.customLeadType === filters.type) 
        : true;
      
      // Фильтр по статусу работы
      const worksMatch = filters.works ? lead.worksWithUs === filters.works : true;
      
      // Фильтр по модели комиссии
      const cmMatch = filters.cm 
        ? (lead.commissionModel === filters.cm || lead.customCommission === filters.cm) 
        : true;
      
      // Менеджеры видят только свои лиды, админы - все
      const ownerMatch = role === "admin" || lead.managerTg === managerTg;
      
      return eventMatch && typeMatch && worksMatch && cmMatch && ownerMatch;
    });
  }, [leads, filters, role, managerTg]);

  // Мемоизированный поиск
  const searchedLeads = useMemo(() => {
    if (!searchQuery.trim()) {
      return filteredLeads;
    }
    
    const query = searchQuery.toLowerCase();
    return filteredLeads.filter((lead) => {
      const searchableText = [
        lead.name,
        lead.company,
        lead.contact,
        lead.event,
        lead.notes || ""
      ].join(" ").toLowerCase();
      
      return searchableText.includes(query);
    });
  }, [filteredLeads, searchQuery]);

  // Опции для фильтров (мемоизированные)
  const filterOptions = useMemo(() => {
    const events = [...new Set(leads.map(l => l.event))].sort();
    
    const types = [...new Set(leads.map(l => 
      l.leadType === "Other" ? (l.customLeadType || "Other") : l.leadType
    ))].sort();
    
    const commissionModels = [...new Set(
      leads
        .map(l => l.commissionModel === "Other" 
          ? (l.customCommission || "Other") 
          : (l.commissionModel || ""))
        .filter(Boolean)
        .map(cm => cm.trim())
    )];
    
    return { events, types, commissionModels };
  }, [leads]);

  const resetFilters = () => {
    setFilters({
      event: "",
      type: "",
      works: "",
      cm: ""
    });
    setSearchQuery("");
  };

  return {
    searchQuery,
    setSearchQuery,
    filters,
    setFilters,
    filteredLeads: searchedLeads,
    filterOptions,
    resetFilters
  };
}
